import type { TenantAccess } from '@konyaxl/shared';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../core/api.js';
import { imageScanSupported, scanImageForCodes } from '../core/imageScan.js';
import { QrDialog } from './QrDialog.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
  /** Üstte gösterilecek sekme şeridi; tek başına açıldığında verilmez. */
  sekmeler?: ReactNode;
  /** Paylaşımdan gelen resim; verilirse dosya seçtirilmeden taranır. */
  baslangicResmi?: Blob | null;
}

interface Kayit {
  id: string;
  codeA: string | null;
  codeB: string | null;
  createdAt: string;
  createdByName: string;
}

interface Satir {
  kod: string;
  format: string;
  durum: 'sorgulanıyor' | 'kayitli' | 'kayitsiz' | 'hata';
  kayit?: Kayit | null;
}

/**
 * Resimden barkod okuma ve sorgulama.
 *
 * Kullanım: WhatsApp'tan gelen bir fotoğrafta birden fazla QR bulunur;
 * hepsi çıkarılır, her biri için kategoride kayıt olup olmadığı sorgulanır.
 * Böylece "bu paketteki kodlardan hangileri sisteme girilmiş" sorusu tek
 * bakışta cevaplanır.
 */
export function ImageScanPage({ tenant, onBack, baslangicResmi, sekmeler }: Props) {
  const [onizleme, setOnizleme] = useState<string | null>(null);
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  const [durum, setDurum] = useState<'bos' | 'taraniyor' | 'bitti'>('bos');
  const [hata, setHata] = useState<string | null>(null);
  const [seciliKayit, setSeciliKayit] = useState<Satir | null>(null);
  const dosyaRef = useRef<HTMLInputElement>(null);

  const resmiIsle = useCallback(
    async (dosya: Blob) => {
      setHata(null);
      setDurum('taraniyor');
      setSatirlar([]);

      // Önceki önizlemenin belleği bırakılır.
      setOnizleme((eski) => {
        if (eski) URL.revokeObjectURL(eski);
        return URL.createObjectURL(dosya);
      });

      try {
        const kodlar = await scanImageForCodes(dosya);

        if (kodlar.length === 0) {
          setDurum('bitti');
          setHata(
            'Resimde barkod bulunamadı. Fotoğrafın net ve kodların tam görünür olduğundan emin olun.',
          );
          return;
        }

        setSatirlar(
          kodlar.map((k) => ({ kod: k.deger, format: k.format, durum: 'sorgulanıyor' })),
        );
        setDurum('bitti');

        // Her kodun kategoride kaydı var mı — sırayla sorgulanır.
        for (const k of kodlar) {
          try {
            const res = await api.get<{ existing: Kayit | null }>(
              `/${tenant.slug}/code-status?code=${encodeURIComponent(k.deger)}`,
            );
            setSatirlar((eski) =>
              eski.map((s) =>
                s.kod === k.deger
                  ? {
                      ...s,
                      durum: res.existing ? 'kayitli' : 'kayitsiz',
                      kayit: res.existing,
                    }
                  : s,
              ),
            );
          } catch {
            setSatirlar((eski) =>
              eski.map((s) => (s.kod === k.deger ? { ...s, durum: 'hata' } : s)),
            );
          }
        }
      } catch (err) {
        setDurum('bitti');
        setHata(err instanceof Error ? err.message : 'Resim işlenemedi');
      }
    },
    [tenant.slug],
  );

  // Paylaşımdan gelen resim varsa doğrudan taranır.
  useEffect(() => {
    if (baslangicResmi) void resmiIsle(baslangicResmi);
  }, [baslangicResmi, resmiIsle]);

  useEffect(() => {
    return () => {
      if (onizleme) URL.revokeObjectURL(onizleme);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Panodan yapıştırma — masaüstünde ekran görüntüsü için pratik. */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const dosya = [...(e.clipboardData?.items ?? [])]
        .find((i) => i.type.startsWith('image/'))
        ?.getAsFile();
      if (dosya) void resmiIsle(dosya);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [resmiIsle]);

  const kayitliSayisi = satirlar.filter((s) => s.durum === 'kayitli').length;
  const kayitsizSayisi = satirlar.filter((s) => s.durum === 'kayitsiz').length;

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Resimden sorgula</h1>
        <div className="ust-bar-sag">
          {satirlar.length > 0 && <span>{satirlar.length} kod</span>}
        </div>
      </header>

      {sekmeler}

      <main className="icerik">
        {!imageScanSupported() ? (
          <div className="bos-durum">
            Bu cihazın tarayıcısı resimden barkod okumayı desteklemiyor.
          </div>
        ) : (
          <>
            <div
              className="resim-birak"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const dosya = e.dataTransfer.files[0];
                if (dosya?.type.startsWith('image/')) void resmiIsle(dosya);
              }}
              onClick={() => dosyaRef.current?.click()}
            >
              {onizleme ? (
                <img src={onizleme} alt="Taranan resim" className="resim-onizleme" />
              ) : (
                <>
                  <div className="resim-ikon">🖼</div>
                  <p>
                    <strong>Resim seçin</strong> ya da buraya sürükleyin
                  </p>
                  <p className="resim-ipucu">
                    Ekran görüntüsünü <kbd>Ctrl+V</kbd> ile de yapıştırabilirsiniz
                  </p>
                </>
              )}
              <input
                ref={dosyaRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const dosya = e.target.files?.[0];
                  if (dosya) void resmiIsle(dosya);
                  e.target.value = '';
                }}
              />
            </div>

            {durum === 'taraniyor' && (
              <p className="sonuc-bilgi">Resim taranıyor…</p>
            )}

            {hata && <div className="hata-kutu">{hata}</div>}

            {satirlar.length > 0 && (
              <>
                <div className="ozet-kartlar">
                  <div className="ozet-kart">
                    <span className="ozet-sayi">{satirlar.length}</span>
                    <span className="ozet-etiket">Bulunan kod</span>
                  </div>
                  <div className="ozet-kart yesil">
                    <span className="ozet-sayi">{kayitliSayisi}</span>
                    <span className="ozet-etiket">Kayıtlı</span>
                  </div>
                  <div className="ozet-kart turuncu">
                    <span className="ozet-sayi">{kayitsizSayisi}</span>
                    <span className="ozet-etiket">Kayıtsız</span>
                  </div>
                </div>

                <table className="kayit-tablo">
                  <thead>
                    <tr>
                      <th>Okunan kod</th>
                      <th>Durum</th>
                      <th>Eşleştiği kod</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satirlar.map((s) => {
                      const eslesen =
                        s.kayit?.codeA === s.kod ? s.kayit?.codeB : s.kayit?.codeA;
                      return (
                        <tr
                          key={s.kod}
                          className="tiklanabilir"
                          onClick={() => setSeciliKayit(s)}
                        >
                          <td className="kod">{s.kod}</td>
                          <td>
                            {s.durum === 'sorgulanıyor' && (
                              <span className="rozet-durum">sorgulanıyor…</span>
                            )}
                            {s.durum === 'kayitli' && (
                              <span className="rozet-durum kayitli">✓ kayıtlı</span>
                            )}
                            {s.durum === 'kayitsiz' && (
                              <span className="rozet-durum kayitsiz">kayıt yok</span>
                            )}
                            {s.durum === 'hata' && (
                              <span className="rozet-durum hata">sorgulanamadı</span>
                            )}
                          </td>
                          <td className="kod">{eslesen ?? '—'}</td>
                          <td>
                            {s.kayit
                              ? new Date(s.kayit.createdAt).toLocaleString('tr-TR', {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </main>

      {seciliKayit && (
        <QrDialog
          kodlar={
            seciliKayit.kayit
              ? [
                  {
                    etiket: tenant.scanRules.a.label,
                    deger: seciliKayit.kayit.codeA ?? '',
                  },
                  {
                    etiket: tenant.scanRules.b.label,
                    deger: seciliKayit.kayit.codeB ?? '',
                  },
                ].filter((k) => k.deger)
              : [{ etiket: 'Okunan kod', deger: seciliKayit.kod }]
          }
          altNot={tenant.name}
          onClose={() => setSeciliKayit(null)}
        />
      )}
    </>
  );
}
